import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import {
  addToAlbum,
  artifactManagerService,
  getUserAlbums,
  memoryUtils,
} from '@fs/zion-memories/memories-authorized-use-only'
import { useHistory } from '@fs/zion-router'
import { useEffectiveUser } from '@fs/zion-user'
import { useOverlay } from '@fs/zion-ui'
import { useFeatureFlag } from '@fs/zion-flags'
import { convertObjectsToIds, getAlbumIdFromPath, filterOutMemories, paths } from './galleryUtils'

const AlbumsContext = createContext()
const AlbumsApiContext = createContext()

const albumActionTypes = {
  addAlbum: 'ADD_ALBUM',
  addBookmarkedAlbum: 'ADD_BOOKMARKED_ALBUM',
  addMemoriesToAlbums: 'ADD_MEMORIES_TO_ALBUMS',
  init: 'INIT',
  removeAlbum: 'REMOVE_ALBUM',
  removeBookmarkedAlbum: 'REMOVE_BOOKMARKED_ALBUM',
  removeMemoriesFromAlbum: 'REMOVE_MEMORIES_FROM_ALBUM',
  removeMemoriesFromAllAlbums: 'REMOVE_MEMORIES_FROM_ALL_ALBUMS',
  setAlbumError: 'SET_ALBUM_ERROR',
  setAlbumMemories: 'SET_ALBUM_MEMORIES',
  setAlbumsLoading: 'SET_ALBUMS_LOADING',
  setSelectedAlbum: 'SET_SELECTED_ALBUM',
  setSelectedAlbumId: 'SET_SELECTED_ALBUM_ID',
  updateAlbum: 'UPDATE_ALBUM',
  updateAlbumMemories: 'UPDATE_ALBUM_MEMORIES',
}

const getAlbumMemories = async (albumId, sortAscending) => {
  const albumOrderResponse = await artifactManagerService.getAlbumOrdinals({
    albumId,
    queryParams: { includeArtifacts: true, includeDatesPlaces: true, includeTotal: true },
  })
  const albumItems = albumOrderResponse?.data?.itemList?.item ?? albumOrderResponse?.data?.itemList?.items ?? []
  const memories = albumItems
    ?.sort(sortAscending ? memoryUtils.ascendingOrdinalSort : memoryUtils.descendingOrdinalSort)
    ?.map((albumItem) => ({ ...albumItem?.artifact, albumItemId: albumItem?.id, ordinal: albumItem?.ordinal }))
  return memories
}

const sortAlbums = (a1, a2) => {
  if (a1.name.toLowerCase() < a2.name.toLowerCase()) return -1
  if (a1.name.toLowerCase() > a2.name.toLowerCase()) return 1
  return 0
}

const findAlbum = (albums, albumId) => albums?.find((a) => a.id === albumId)

const isAlbumError = (index, albumsResponses) =>
  albumsResponses?.[index]?.status === 'rejected' || albumsResponses?.[index]?.value?.albumsError

const albumReducer = (state, action) => {
  switch (action.type) {
    case albumActionTypes.addAlbum: {
      if (!action.newAlbum) return state

      const albums = [...state.albums, action.newAlbum].sort(sortAlbums)
      return { ...state, albums }
    }
    case albumActionTypes.addBookmarkedAlbum:
      return { ...state, bookmarkedAlbums: [...state.bookmarkedAlbums, state.selectedAlbum] }
    case albumActionTypes.addMemoriesToAlbums: {
      const { albums, memories } = action

      const memoryIds = memories.map((memory) => memory.id)
      const newAlbumMemoriesMap = {}
      const newAlbumMemoryIdsMap = { ...state.albumMemoryIdsMap }

      /** Add all of the memories to each album and increment the memory count for each album. */
      albums.forEach((album) => {
        /** Add all of the memories to the album.
         *
         * NOTE:  (See Jira EOLBFRST-2188)
         * If the album isn't in `albumMemoriesMap`, then the user hasn't clicked on this
         * album link to load the albums memories. In this case, we shouldn't add the album to the
         * map because this will prevent the rest of the albums memories from being loaded when the
         * user clicks on the album link for the first because it will think the album memories have
         * already been loaded. */
        const albumMemories = state.albumMemoriesMap?.[album.id]
        if (albumMemories) {
          newAlbumMemoriesMap[album.id] = [...albumMemories, ...memories]
        }

        /** Update the albumMemoryIdsMap for the album. */
        const albumMemoryIds = state.albumMemoryIdsMap?.[album.id] ?? []
        newAlbumMemoryIdsMap[album.id] = [...albumMemoryIds, ...memoryIds]

        /** Increment the memory count for the album. */
        const albumToUpdate = findAlbum(state.albums, album.id)
        albumToUpdate && (albumToUpdate.artifactCount += memories.length)
      })

      const newState = {
        ...state,
        albumMemoriesMap: { ...state.albumMemoriesMap, ...newAlbumMemoriesMap },
        albumMemoryIdsMap: { ...state.albumMemoryIdsMap, ...newAlbumMemoryIdsMap },
        albumErrorsMap: {},
        albums: [...state.albums],
      }

      return newState
    }
    case albumActionTypes.init:
      return {
        ...state,
        albums: action.albums,
        albumErrors: action.albumErrors,
        albumMemoryIdsMap: action.albumMemoryIdsMap,
        bookmarkedAlbums: action.bookmarkedAlbums,
        albumsLoading: false,
      }
    case albumActionTypes.removeAlbum: {
      if (!action.album) return state

      const albums = state.albums.filter((album) => album.id !== action.album.id)
      return { ...state, albums, selectedAlbumId: null }
    }
    case albumActionTypes.removeBookmarkedAlbum:
      return {
        ...state,
        bookmarkedAlbums: state.bookmarkedAlbums.filter((album) => album.id !== state.selectedAlbum.id),
      }
    case albumActionTypes.removeMemoriesFromAlbum: {
      /** Decrement the memory count for the selected album. */
      const album = findAlbum(state.albums, action.albumId)
      album && (album.artifactCount -= action.removedMemoryIds.length)

      /** Update albumMemoryIdsMap by filtering out the removed memory IDs for the specified album. */
      const newAlbumMemoryIdsMap = {
        ...state.albumMemoryIdsMap,
        [action.albumId]: state.albumMemoryIdsMap?.[action.albumId]?.filter(
          (id) => !action.removedMemoryIds.includes(id)
        ),
      }

      /** If the user has loaded the album memories, update albumMemoriesMap with the remaining memories for the specified album. */
      let newAlbumMemoriesMap = state.albumMemoriesMap
      if (state.albumMemoriesMap?.[action.albumId]) {
        /** Get all of the memories in the album. */
        const albumMemories = state.albumMemoriesMap[action.albumId] ?? []

        /** Filter out the memories removed from the album. */
        const remainingAlbumMemories = albumMemories.filter((memory) => !action.removedMemoryIds.includes(memory.id))

        newAlbumMemoriesMap = { ...state.albumMemoriesMap, [action.albumId]: remainingAlbumMemories }
      }

      return {
        ...state,
        albumMemoriesMap: newAlbumMemoriesMap,
        albumMemoryIdsMap: newAlbumMemoryIdsMap,
        albumErrorsMap: {},
        albums: [...state.albums],
      }
    }
    case albumActionTypes.removeMemoriesFromAllAlbums: {
      const deletedIds = convertObjectsToIds(action.memories)

      /** Update albumMemoriesMap by filtering out the deleted memories. */
      const albumMemoriesMap = Object.keys(state.albumMemoriesMap).reduce(
        (map, albumId) => ({
          ...map,
          [albumId]: filterOutMemories(state.albumMemoriesMap?.[albumId], deletedIds),
        }),
        {}
      )

      /** Update albumMemoryIdsMap by filtering out the deleted memory IDs. */
      const albumMemoryIdsMap = Object.keys(state.albumMemoryIdsMap).reduce(
        (map, albumId) => ({
          ...map,
          [albumId]: state.albumMemoryIdsMap?.[albumId]?.filter((id) => !deletedIds.includes(id)),
        }),
        {}
      )

      /** Update albums with the new memory counts.
       *
       *  NOTE: We should also update bookmarked albums, but Artifact Manager doesn't have an endpoint to get
       * the list of memory ids for favorited albums. It only has one for user owned albums. */
      const albums = state.albums.map((album) => {
        album.artifactCount = albumMemoryIdsMap[album.id].length
        return album
      })

      return {
        ...state,
        albumErrorsMap: {},
        albumMemoriesMap,
        albumMemoryIdsMap,
        albums,
      }
    }
    case albumActionTypes.setAlbumError: {
      const { albumId, albumError } = action
      return { ...state, albumErrorsMap: { ...state.albumErrorsMap, [albumId]: albumError } }
    }
    case albumActionTypes.setAlbumsLoading:
      return { ...state, albumsLoading: action.albumsLoading }
    case albumActionTypes.setAlbumMemories: {
      const { albumId, memories } = action
      return {
        ...state,
        albumMemoriesMap: { ...state.albumMemoriesMap, [albumId]: memories },
        albumErrorsMap: { ...state.albumErrorsMap, [albumId]: false },
      }
    }
    case albumActionTypes.setSelectedAlbum:
      return { ...state, selectedAlbum: action.selectedAlbum, selectedAlbumId: action.selectedAlbum?.id }
    case albumActionTypes.setSelectedAlbumId: {
      const { selectedAlbumId } = action
      const selectedAlbum = findAlbum([...state.albums, ...state.bookmarkedAlbums], selectedAlbumId)
      return { ...state, selectedAlbumId, selectedAlbum }
    }
    case albumActionTypes.updateAlbumMemories:
      return {
        ...state,
        /**
         * If the updated memory is in an album, then it needs to be replaced in the albumMemoriesMap.
         */
        albumMemoriesMap: Object.keys(state.albumMemoriesMap).reduce((map, albumId) => {
          const memoryInAlbum = state.albumMemoriesMap?.[albumId]?.some(
            (albumMemory) => albumMemory?.id === action?.memory?.id
          )
          return memoryInAlbum
            ? {
                ...map,
                [albumId]: state.albumMemoriesMap?.[albumId]?.map((memory) =>
                  memory?.id === action?.memory?.id ? action?.memory : memory
                ),
              }
            : map
        }, {}),
      }
    case albumActionTypes.updateAlbum: {
      if (!action.updatedAlbum) return state

      const editedAlbumIndex = state.albums.findIndex((existingAlbum) => existingAlbum.id === action.updatedAlbum.id)
      const newAlbums = [...state.albums]
      newAlbums[editedAlbumIndex] = action.updatedAlbum
      const sortedNewAlbums = newAlbums.sort(sortAlbums)

      return { ...state, albums: sortedNewAlbums }
    }
    default:
      return state
  }
}

const initialState = {
  albumErrors: false,
  albumErrorsMap: {},
  albumMemoriesMap: {},
  albumMemoryIdsMap: {},
  albums: [],
  albumsLoading: true,
  bookmarkedAlbums: [],
  selectedAlbum: null,
  selectedAlbumId: null,
}

const AlbumsProvider = ({
  children,
  galleryActions,
  isAlbumPage,
  isMobile,
  isMyMemoriesPage,
  memoriesLoading,
  sortAscending,
}) => {
  const isCurrent = useRef(true)
  const [state, dispatch] = useReducer(albumReducer, initialState)
  const history = useHistory()
  const { cisId, signedIn } = useEffectiveUser()
  const editAlbumOverlay = useOverlay()
  const { isOn: dragAndDropFlag } = useFeatureFlag('memories_gallery_dnd')

  const { isOpen: editAlbumOverlayOpen } = editAlbumOverlay
  const {
    addMemories,
    removeMemories,
    setActivePage,
    setMemoriesLoading,
    setSelectMode,
    updateMemories,
    updateMemory,
  } = galleryActions

  const enableDragAndDrop = useMemo(
    () => dragAndDropFlag && !isMobile && signedIn && !editAlbumOverlayOpen,
    [dragAndDropFlag, editAlbumOverlayOpen, isMobile, signedIn]
  )

  const allAlbums = useMemo(() => [...state.albums, ...state.bookmarkedAlbums], [state.albums, state.bookmarkedAlbums])

  const selectedAlbumIsBookmarked = useMemo(
    () => state.bookmarkedAlbums?.some((album) => album.id === state.selectedAlbumId),
    [state.bookmarkedAlbums, state.selectedAlbumId]
  )

  const albumActions = useMemo(
    () => ({
      addAlbum: (newAlbum) => dispatch({ type: albumActionTypes.addAlbum, newAlbum }),
      addBookmarkedAlbum: () => dispatch({ type: albumActionTypes.addBookmarkedAlbum }),
      addMemoriesToAlbums: ({ albums, memories }) =>
        dispatch({ type: albumActionTypes.addMemoriesToAlbums, albums, memories }),
      init: ({ albums, albumErrors, albumMemoryIdsMap, bookmarkedAlbums }) =>
        dispatch({ type: albumActionTypes.init, albums, albumErrors, albumMemoryIdsMap, bookmarkedAlbums }),
      removeAlbum: (album) => dispatch({ type: albumActionTypes.removeAlbum, album }),
      removeBookmarkedAlbum: () => dispatch({ type: albumActionTypes.removeBookmarkedAlbum }),
      removeMemoriesFromAlbum: ({ albumId, removedMemoryIds }) =>
        dispatch({ type: albumActionTypes.removeMemoriesFromAlbum, albumId, removedMemoryIds }),
      removeMemoriesFromAllAlbums: ({ memories }) =>
        dispatch({ type: albumActionTypes.removeMemoriesFromAllAlbums, memories }),
      setAlbumError: ({ albumId, albumError }) =>
        dispatch({ type: albumActionTypes.setAlbumError, albumId, albumError }),
      setAlbumsLoading: (albumsLoading) => dispatch({ type: albumActionTypes.setAlbumsLoading, albumsLoading }),
      setAlbumMemories: ({ albumId, memories }) =>
        dispatch({ type: albumActionTypes.setAlbumMemories, albumId, memories }),
      setSelectedAlbum: (selectedAlbum) => dispatch({ type: albumActionTypes.setSelectedAlbum, selectedAlbum }),
      setSelectedAlbumId: (selectedAlbumId) => dispatch({ type: albumActionTypes.setSelectedAlbumId, selectedAlbumId }),
      updateAlbumMemories: (memory) => dispatch({ type: albumActionTypes.updateAlbumMemories, memory }),
      updateAlbum: (updatedAlbum) => dispatch({ type: albumActionTypes.updateAlbum, updatedAlbum }),
    }),
    []
  )

  const fetchAlbums = useCallback(async (apiParams) => {
    let albums = []
    let albumsError = false
    try {
      albums = (await getUserAlbums(apiParams)) ?? []
    } catch (error) {
      albumsError = true
    }
    return { albums, albumsError }
  }, [])

  const fetchMemoryIdsInAlbums = useCallback(async (apiParams) => {
    let memoryIdsInAlbums = []
    try {
      memoryIdsInAlbums = (await artifactManagerService.getMemoryIdsInAlbums(apiParams)) ?? []
    } catch (error) {
      console.error(error)
    }
    return memoryIdsInAlbums
  }, [])

  const albumMemories = useMemo(
    () => state.albumMemoriesMap?.[state.selectedAlbumId] ?? [],
    [state.albumMemoriesMap, state.selectedAlbumId]
  )

  const isMemoryInAnyAlbum = useCallback(
    (memoryId) => Object.values(state.albumMemoryIdsMap).some((memoryIds) => memoryIds.includes(memoryId)),
    [state.albumMemoryIdsMap]
  )

  /**
   * This gallery callback is defined here since it only alters album related state.
   */
  const addToAlbumsCallback = useCallback(
    (params) => {
      albumActions.addMemoriesToAlbums(params)
      setSelectMode(false)
    },
    [albumActions, setSelectMode]
  )

  const removeFromAlbumCallback = useCallback(
    (params) => {
      albumActions.removeMemoriesFromAlbum(params)
      setSelectMode(false)
    },
    [albumActions, setSelectMode]
  )

  /**
   * Callback for adding memories to the Gallery and selected album after uploading.
   */
  const addCallback = useCallback(
    async ({ memories }) => {
      if (state.selectedAlbum) {
        const albumResults = await addToAlbum({ albums: [state.selectedAlbum], memories })
        albumResults.successful.length && albumActions.addMemoriesToAlbums({ albums: [state.selectedAlbum], memories })
      }

      addMemories({ memories })

      /** If memories were added on a page that isn't My Memories or an album, navigate to the My Memories page. */
      if (!isMyMemoriesPage && !isAlbumPage) {
        const path = paths.gallery
        history.push(path)
        setActivePage(path)
      }
    },
    [addMemories, albumActions, history, isAlbumPage, isMyMemoriesPage, setActivePage, state.selectedAlbum]
  )

  /**
   * This gallery callback needs to be in the album context and not the gallery context because
   * if a memory in an album is deleted it needs to handle filtering that out of state too.
   */
  const deleteCallback = useCallback(
    (params) => {
      albumActions.removeMemoriesFromAllAlbums(params)
      removeMemories(params)
    },
    [albumActions, removeMemories]
  )

  /**
   * This gallery callback needs to be in the album context in case the memory
   * that is updated is also in an album.
   */
  const updateCallback = useCallback(
    (updatedMemories) => {
      if (updatedMemories.length === 1) {
        const inAlbum = isMemoryInAnyAlbum(updatedMemories[0]?.id)
        inAlbum && albumActions.updateAlbumMemories(updatedMemories[0])
        updateMemory(updatedMemories[0])
      } else {
        updatedMemories.forEach((updatedMemory) => {
          const inAlbum = isMemoryInAnyAlbum(updatedMemory?.id)
          inAlbum && albumActions.updateAlbumMemories(updatedMemory)
        })
        updateMemories(updatedMemories)
      }
    },
    [albumActions, isMemoryInAnyAlbum, updateMemories, updateMemory]
  )

  const handleEditSuccess = useCallback(
    (updatedMemories) => {
      updateCallback(updatedMemories)
      galleryActions.endEditing()
      galleryActions.setSelectMode(false)
    },
    [galleryActions, updateCallback]
  )

  useEffect(() => {
    const loadAlbums = async () => {
      if (cisId) {
        albumActions.setAlbumsLoading(true)

        const respIndexes = {
          userAlbums: 0,
          bookmarkedAlbums: 1,
          memoryIdsInAlbums: 2,
        }
        const albumsResponses = await Promise.allSettled([
          fetchAlbums({ cisId }),
          fetchAlbums({ cisId, bookmarked: true }),
          fetchMemoryIdsInAlbums({ cisId }),
        ])

        const newAlbums = albumsResponses?.[respIndexes.userAlbums]?.value?.albums
        const newBookmarkedAlbums = albumsResponses?.[respIndexes.bookmarkedAlbums]?.value?.albums
        const newAlbumMemoryIdsMap = albumsResponses?.[respIndexes.memoryIdsInAlbums]?.value?.data.reduce(
          (acc, { artifactId: memoryId, albumIds }) => {
            albumIds.forEach((albumId) => {
              if (!acc[albumId]) {
                acc[albumId] = []
              }
              acc[albumId].push(memoryId)
            })
            return acc
          },
          {}
        )

        /**
         * At this point, the newAlbumMemoryIdsMap does not include entries for albumIds without any memories.
         * This creates entries for those albumIds with an empty array as the value.
         */
        const allAlbumIds = newAlbums.map((album) => album.id)
        allAlbumIds.forEach((albumId) => {
          if (!newAlbumMemoryIdsMap[albumId]) {
            newAlbumMemoryIdsMap[albumId] = []
          }
        })

        isCurrent.current &&
          albumActions.init({
            albums: newAlbums,
            albumErrors:
              isAlbumError(respIndexes.userAlbums, albumsResponses) ||
              isAlbumError(respIndexes.bookmarkedAlbums, albumsResponses),
            albumMemoryIdsMap: newAlbumMemoryIdsMap,
            bookmarkedAlbums: newBookmarkedAlbums,
          })
      } else {
        albumActions.setAlbumsLoading(false)
      }
    }

    loadAlbums()
  }, [albumActions, cisId, fetchAlbums, fetchMemoryIdsInAlbums])

  const loadAlbumMemories = useCallback(
    async (albumId) => {
      if (!memoriesLoading && !state.albumMemoriesMap[albumId]) {
        try {
          setMemoriesLoading(true)
          const memories = await getAlbumMemories(albumId, sortAscending)
          isCurrent.current && albumActions.setAlbumMemories({ albumId, memories })
        } catch (error) {
          isCurrent.current && albumActions.setAlbumError({ albumId, albumError: true })
          console.error(error)
        } finally {
          isCurrent.current && setMemoriesLoading(false)
        }
      }
    },
    [albumActions, memoriesLoading, setMemoriesLoading, sortAscending, state.albumMemoriesMap]
  )

  /**
   * The purpose of this useEffect is to load the album data and set the album as the `selectedAlbum` only
   * when the browser is loading an album page directly and not because the user clicked on an album link.
   * Clicking on an album link is handled in GalleryExplorer.
   *
   * This will get the album id from the url when the page first loads, load the album data, and then set
   * the album as the `selectedAlubm.
   */
  useEffect(() => {
    const loadAlbumFromUrl = async () => {
      const pathname = history.location.pathname
      const albumId = getAlbumIdFromPath(pathname)

      /**
       * If this album is already set as the selected album, then there's nothing more to do, so just return.
       * This would be the case if this useEffect has already run, or the user has already clicked on the link
       * for this album.
       */
      if (albumId === state.selectedAlbum?.id) {
        return
      }

      /**
       * If the albumId in the URL isn't the `selectedAlbum` then load the album data and set it as
       * the `selectedAlbum`.
       */
      if (albumId) {
        try {
          const response = await artifactManagerService.getAlbum({ albumId })
          const album = response.data

          if (isCurrent.current) {
            albumActions.setSelectedAlbum(album)
          }
        } catch (error) {
          console.error(error)
        }
      }
    }

    loadAlbumFromUrl()
  }, [albumActions, allAlbums, history.location.pathname, state.selectedAlbum?.id])

  /**
   * The purpose of this useEffect is to load the album memories only when the browser is loading an album
   * page directly and not because the user clicked on an album link. Clicking on an album link is handled
   * in GalleryExplorer.
   *
   * This will get the album id from the url when the page first loads, and then load the album's memories.
   */
  useEffect(() => {
    const pathname = history.location.pathname
    const albumId = getAlbumIdFromPath(pathname)

    /** If the memories for this album are already loaded, then just return. */
    if (state.albumMemoriesMap[albumId]) {
      return
    }

    if (albumId && !memoriesLoading) {
      loadAlbumMemories(albumId)
    } else if (!albumId) {
      /** If the album path is missing the albumId, go to the gallery. */
      history.replace(paths.gallery)
    }
  }, [history, loadAlbumMemories, memoriesLoading, state.albumMemoriesMap])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  const apiValues = useMemo(
    () => ({
      ...albumActions,
      addCallback,
      addToAlbumsCallback,
      deleteCallback,
      findAlbum,
      handleEditSuccess,
      isMemoryInAnyAlbum,
      loadAlbumMemories,
      removeFromAlbumCallback,
      updateCallback,
    }),
    [
      addCallback,
      addToAlbumsCallback,
      albumActions,
      deleteCallback,
      handleEditSuccess,
      isMemoryInAnyAlbum,
      loadAlbumMemories,
      removeFromAlbumCallback,
      updateCallback,
    ]
  )

  const {
    albumErrors,
    albumErrorsMap,
    albumMemoriesMap,
    albumMemoryIdsMap,
    albums,
    albumsLoading,
    bookmarkedAlbums,
    selectedAlbum,
    selectedAlbumId,
  } = state

  const dataValues = useMemo(
    () => ({
      albumErrors,
      albumErrorsMap,
      albumMemories,
      albumMemoriesMap,
      albumMemoryIdsMap,
      albums,
      albumsLoading,
      bookmarkedAlbums,
      editAlbumOverlay,
      enableDragAndDrop,
      selectedAlbum,
      selectedAlbumId,
      selectedAlbumIsBookmarked,
    }),
    [
      albumErrors,
      albumErrorsMap,
      albumMemories,
      albumMemoriesMap,
      albumMemoryIdsMap,
      albums,
      albumsLoading,
      bookmarkedAlbums,
      editAlbumOverlay,
      enableDragAndDrop,
      selectedAlbum,
      selectedAlbumId,
      selectedAlbumIsBookmarked,
    ]
  )

  return (
    <AlbumsContext.Provider value={dataValues}>
      <AlbumsApiContext.Provider value={apiValues}>{children}</AlbumsApiContext.Provider>
    </AlbumsContext.Provider>
  )
}

export { AlbumsApiContext, AlbumsContext, AlbumsProvider }
